import React from "react";
import { createPortal } from "react-dom";
import { DirectoryMember, getMembers } from "../../../management/board/people-directory";
import { extractMentionToken, renderAvatar } from "./comments-panel-logic";

// Controlled <textarea> with @mention autocomplete; insertion keeps the "@Full Name" form the rest of the comments UI matches on (highlightMentions / extractMentionedUserIds).
interface IMentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  // Escape when the mention picker is closed (host cancel, e.g. dismiss an edit/reply composer).
  onCancel?: () => void;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  // Stable design-time hook for E2E tests; the host composer owns the id it passes in.
  dataTestId?: string;
}

interface IMentionTextareaState {
  open: boolean;
  query: string;
  anchorIndex: number;
  highlight: number;
  top: number;
  left: number;
}

export class MentionTextarea extends React.Component<IMentionTextareaProps, IMentionTextareaState> {
  private ref: React.RefObject<HTMLTextAreaElement> = React.createRef<HTMLTextAreaElement>();

  constructor(props: IMentionTextareaProps) {
    super(props);
    this.state = { open: false, query: "", anchorIndex: -1, highlight: 0, top: 0, left: 0 };
  }

  private candidates(): DirectoryMember[] {
    const members: DirectoryMember[] = getMembers();
    const q: string = this.state.query.toLowerCase();
    if (!q) return members.slice(0, 8);
    return members.filter((m: DirectoryMember) => m.name.toLowerCase().includes(q)).slice(0, 8);
  }

  private detect(value: string): void {
    const el: HTMLTextAreaElement | null = this.ref.current;
    const cursor: number = el?.selectionStart ?? value.length;
    const token: string | null = extractMentionToken(value, cursor);
    if (token !== null) {
      const rect: DOMRect | undefined = el?.getBoundingClientRect();
      this.setState({
        open: true,
        query: token,
        anchorIndex: cursor - token.length - 1,
        highlight: 0,
        top: rect?.top ?? 0,
        left: rect?.left ?? 0
      });
    } else if (this.state.open) {
      this.setState({ open: false, query: "" });
    }
  }

  private handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value: string = e.target.value;
    this.props.onChange(value);
    this.detect(value);
  };

  private insert(member: DirectoryMember): void {
    const { value, onChange } = this.props;
    const { anchorIndex, query } = this.state;
    const before: string = value.slice(0, anchorIndex + 1);
    const after: string = value.slice(anchorIndex + 1 + query.length);
    onChange(`${before}${member.name} ${after}`);
    this.setState({ open: false, query: "" });
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (this.state.open) {
      const cands: DirectoryMember[] = this.candidates();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setState((prev: IMentionTextareaState) => ({ highlight: Math.min(prev.highlight + 1, cands.length - 1) }));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setState((prev: IMentionTextareaState) => ({ highlight: Math.max(prev.highlight - 1, 0) }));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const member: DirectoryMember | undefined = cands[this.state.highlight];
        if (member) this.insert(member);
        return;
      }
      if (e.key === "Escape") {
        // Close the picker without letting the host (e.g. the canvas composer) also treat Escape
        // as cancel.
        e.preventDefault();
        e.stopPropagation();
        this.setState({ open: false, query: "" });
        return;
      }
    }
    // Picker is closed here: Escape cancels the host composer; Enter submits.
    if (e.key === "Escape") {
      if (this.props.onCancel) {
        e.preventDefault();
        this.props.onCancel();
      }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && this.props.onSubmit) {
      e.preventDefault();
      this.props.onSubmit();
    }
  };

  // Close the picker when focus leaves the field (outside click). Item clicks use onMouseDown +
  // preventDefault, so selecting a suggestion doesn't blur the textarea first.
  private handleBlur = (): void => {
    if (this.state.open) this.setState({ open: false, query: "" });
  };

  render(): React.ReactNode {
    const { value, placeholder, rows, autoFocus, disabled, className, dataTestId } = this.props;
    const { open, top, left, highlight } = this.state;
    const cands: DirectoryMember[] = open ? this.candidates() : [];
    return (
      <>
        <textarea
          ref={this.ref}
          data-testid={dataTestId}
          className={className}
          value={value}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          onBlur={this.handleBlur}
          placeholder={placeholder}
          rows={rows}
          autoFocus={autoFocus}
          disabled={disabled}
        />
        {open &&
          cands.length > 0 &&
          createPortal(
            <div className="comments-panel__mention-popup" style={{ bottom: window.innerHeight - top + 6, left }}>
              {cands.map((m: DirectoryMember, i: number) => (
                <div
                  key={m.id}
                  className={`comments-panel__mention-item${i === highlight ? " comments-panel__mention-item--active" : ""}`}
                  onMouseDown={(e: React.MouseEvent): void => {
                    e.preventDefault();
                    this.insert(m);
                  }}
                >
                  {renderAvatar(m.id, m.name.split(" ")[0], m.name.split(" ")[1], m.name, "sm")}
                  <span className="comments-panel__mention-name">{m.name}</span>
                </div>
              ))}
            </div>,
            document.body
          )}
      </>
    );
  }
}
